import { useRequiredContext } from "@redotech/react-util/context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { breadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import { memo, ReactNode } from "react";
import { matchPath, Navigate } from "react-router-dom";

const supplierDetailsBreadcrumb = breadcrumbSlot();
const purchaseOrderBreadcrumb = breadcrumbSlot();

const HIDE_PADDING_ROUTES: string[] = [
  "/products",
  "/products/:productId",
  "/products/adjustments",
  "/products/forecasting",
  "/liquidation/products",
  "/suppliers",
  "/suppliers/:supplierId",
  "/purchase-orders",
  "/purchase-orders/:purchaseOrderId",
];

function hidePadding(url: string): boolean {
  return HIDE_PADDING_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/inventory${route}`, url),
  );
}

const FULLSCREEN_ROUTES: string[] = [];

const HIDE_HEADER_ROUTES: string[] = [...FULLSCREEN_ROUTES];
function hideHeader(url: string): boolean {
  return HIDE_HEADER_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/inventory${route}`, url),
  );
}

const HIDE_NAV_ROUTES: string[] = [...FULLSCREEN_ROUTES];

function hideNav(url: string): boolean {
  return HIDE_NAV_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/inventory${route}`, url),
  );
}

export const inventoryRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Inventory" },
    async lazy() {
      const { Page } = await import("../../page");
      return {
        element: (
          <Page
            hideHeader={hideHeader}
            hideNavbar={hideNav}
            hidePadding={hidePadding}
          />
        ),
      };
    },
    children: [
      { index: true, element: <Navigate replace to="products" /> },
      {
        path: "products",
        handle: { breadcrumb: "Products" },
        children: [
          {
            index: true,
            handle: "Products",
            async lazy() {
              const { ProductFamilies } = await import(
                "@redotech/redo-merchant-inventory-app/product-families"
              );
              return { Component: ProductFamilies };
            },
          },
          {
            path: ":productId",
            handle: { breadcrumb: "Product Details" },
            async lazy() {
              const { RedoProductDetail } = await import(
                "@redotech/redo-merchant-inventory-app/products/redo-product-detail"
              );
              return { Component: RedoProductDetail };
            },
          },
          {
            path: "pricing-rules",
            handle: { breadcrumb: "Pricing Rules" },
            async lazy() {
              const { PricingRules } = await import(
                "@redotech/redo-merchant-inventory-app/pricing-rules/pricing-rules"
              );
              return { Component: PricingRules };
            },
          },
          {
            path: "adjustments",
            handle: { breadcrumb: "Inventory Adjustments" },
            async lazy() {
              const { InventoryAdjustments } = await import(
                "@redotech/redo-merchant-inventory-app/adjustments/inventory-adjustments"
              );
              return { Component: InventoryAdjustments };
            },
          },
          {
            path: "forecasting",
            handle: { breadcrumb: "Forecasting" },
            async lazy() {
              const { Forecasting } = await import(
                "@redotech/redo-merchant-inventory-app/forecasting/forecasting"
              );
              return { Component: Forecasting };
            },
          },
        ],
      },
      {
        path: "suppliers",
        handle: { breadcrumb: "Suppliers" },
        children: [
          {
            index: true,
            async lazy() {
              const { SuppliersPage } = await import(
                "@redotech/redo-merchant-inventory-app/suppliers/suppliers"
              );
              return { Component: SuppliersPage };
            },
          },
          {
            path: ":supplierId",
            handle: { breadcrumb: supplierDetailsBreadcrumb },
            async lazy() {
              const { SupplierDetailsPage } = await import(
                "@redotech/redo-merchant-inventory-app/suppliers/supplier-detail"
              );
              return {
                element: (
                  <SupplierDetailsPage breadcrumb={supplierDetailsBreadcrumb} />
                ),
              };
            },
          },
        ],
      },
      {
        path: "purchase-orders",
        handle: { breadcrumb: "Purchase Orders" },
        children: [
          {
            index: true,
            async lazy() {
              const { PurchaseOrdersPage } = await import(
                "@redotech/redo-merchant-inventory-app/purchase-orders/purchase-orders"
              );
              return { Component: PurchaseOrdersPage };
            },
          },
          {
            path: ":purchaseOrderId",
            handle: { breadcrumb: purchaseOrderBreadcrumb },
            async lazy() {
              const { PurchaseOrderPage } = await import(
                "@redotech/redo-merchant-inventory-app/purchase-orders/purchase-order-detail"
              );
              return {
                element: (
                  <PurchaseOrderPage breadcrumb={purchaseOrderBreadcrumb} />
                ),
              };
            },
          },
        ],
      },
      // {
      //   path: "product-families/:productFamilyId",
      //   handle: { breadcrumb: "Product Details" },
      //   async lazy() {
      //     const { ProductFamilyDetail } = await import(
      //       "@redotech/redo-merchant-inventory-app/product-families/product-family-detail"
      //     );
      //     return { Component: ProductFamilyDetail };
      //   },
      // },
      {
        path: "liquidation/dashboard",
        handle: { breadcrumb: "Home" },
        async lazy() {
          const { LiquidationPage } = await import(
            "@redotech/redo-merchant-inventory-app/liquidation/liquidation-page"
          );
          return {
            Component: () => (
              <MarketplaceAccessGuard>
                <LiquidationPage />
              </MarketplaceAccessGuard>
            ),
          };
        },
      },
      {
        path: "liquidation/products",
        handle: { breadcrumb: "All Listings" },
        async lazy() {
          const { MarketplaceListings } = await import(
            "@redotech/redo-merchant-inventory-app/marketplace/marketplace-listings"
          );
          return {
            Component: () => (
              <MarketplaceAccessGuard>
                <MarketplaceListings />
              </MarketplaceAccessGuard>
            ),
          };
        },
      },
    ],
  },
];

export const MarketplaceAccessGuard = memo(function MarketplaceAccessGuard({
  children,
}: {
  children: ReactNode;
}) {
  const team = useRequiredContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);

  const isMarketplaceEnabled = team.settings.recommerce?.marketplace ?? false;

  if (!isMarketplaceEnabled) {
    // Redirect to main inventory page if marketplace is not enabled
    return <Navigate replace to={routes.inventoryManagementBaseUrl} />;
  }

  return <>{children}</>;
});
